import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { setAccent } from '@theonefamily/ui';
import App from './App.js';

// Protect accent: violet #8b5cf6
setAccent('#8b5cf6');
import { DashboardPage } from './pages/DashboardPage.js';
import { DarkWebPage } from './pages/DarkWebPage.js';
import { ShadowAIPage } from './pages/ShadowAIPage.js';
import { SaasPosturePage } from './pages/SaasPosturePage.js';
import { DmarcReportsPage } from './pages/DmarcReportsPage.js';
import { EmailDomainsPage } from './pages/EmailDomainsPage.js';
import { EmailAlertsPage } from './pages/EmailAlertsPage.js';
import { PhishingCampaignsPage } from './pages/PhishingCampaignsPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { CasbDashboardPage } from './pages/CasbDashboardPage.js';
import { CasbEventsPage } from './pages/CasbEventsPage.js';
import { CasbUserRiskPage } from './pages/CasbUserRiskPage.js';
import { ConditionalAccessPage } from './pages/ConditionalAccessPage.js';
import { KnownLocationsPage } from './pages/KnownLocationsPage.js';
import { AccessAuditPage } from './pages/AccessAuditPage.js';
import { BreachResponsePage } from './pages/BreachResponsePage.js';
import { AdaptiveLearningPage } from './pages/AdaptiveLearningPage.js';
import { MspRiskPage } from './pages/MspRiskPage.js';
import { InsiderThreatPage } from './pages/InsiderThreatPage.js';
import { DmarcJourneyPage } from './pages/DmarcJourneyPage.js';
import { ComplianceTrainingPage } from './pages/ComplianceTrainingPage.js';
import { VishingPage } from './pages/VishingPage.js';
import { SecurityBriefingPage } from './pages/SecurityBriefingPage.js';
import { LookalikeRegistrationsPage } from './pages/LookalikeRegistrationsPage.js';
import { BecSimulationPage } from './pages/BecSimulationPage.js';
import { SecurityCulturePage } from './pages/SecurityCulturePage.js';
import { UserOnboardingPage } from './pages/UserOnboardingPage.js';
import { SupplyChainRiskPage } from './pages/SupplyChainRiskPage.js';
import { CredentialExposurePage } from './pages/CredentialExposurePage.js';
import { ThreatNarrativePage } from './pages/ThreatNarrativePage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';
import './index.css';

const CypherAdminPage = lazy(() => import('./pages/CypherAdminPage.js'));
const CypherSetupPage = lazy(() => import('./pages/CypherSetupPage.js'));

function PageFallback() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <Routes>
        <Route element={<App />}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="dark-web" element={<DarkWebPage />} />
          <Route path="shadow-ai" element={<ShadowAIPage />} />
          <Route path="saas-posture" element={<SaasPosturePage />} />
          <Route path="dmarc" element={<DmarcReportsPage />} />
          <Route path="email-domains" element={<EmailDomainsPage />} />
          <Route path="email-alerts" element={<EmailAlertsPage />} />
          <Route path="phishing" element={<PhishingCampaignsPage />} />
          <Route path="casb" element={<CasbDashboardPage />} />
          <Route path="casb/events" element={<CasbEventsPage />} />
          <Route path="casb/user-risk" element={<CasbUserRiskPage />} />
          <Route path="zero-trust" element={<ConditionalAccessPage />} />
          <Route path="zero-trust/locations" element={<KnownLocationsPage />} />
          <Route path="zero-trust/audit" element={<AccessAuditPage />} />
          <Route path="breach-response" element={<BreachResponsePage />} />
          <Route path="adaptive-learning" element={<AdaptiveLearningPage />} />
          <Route path="msp-risk" element={<MspRiskPage />} />
          <Route path="insider-threat" element={<InsiderThreatPage />} />
          <Route path="dmarc-journey" element={<DmarcJourneyPage />} />
          <Route path="compliance-training" element={<ComplianceTrainingPage />} />
          <Route path="vishing" element={<VishingPage />} />
          <Route path="security-briefing" element={<SecurityBriefingPage />} />
          <Route path="lookalike-domains" element={<LookalikeRegistrationsPage />} />
          <Route path="bec-simulation" element={<BecSimulationPage />} />
          <Route path="security-culture" element={<SecurityCulturePage />} />
          <Route path="user-onboarding" element={<UserOnboardingPage />} />
          <Route path="supply-chain-risk" element={<SupplyChainRiskPage />} />
          <Route path="credential-exposure" element={<CredentialExposurePage />} />
          <Route path="threat-narrative" element={<ThreatNarrativePage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="cypher" element={<Suspense fallback={<PageFallback />}><CypherAdminPage /></Suspense>} />
          <Route path="cypher/setup" element={<Suspense fallback={<PageFallback />}><CypherSetupPage /></Suspense>} />
          <Route path="cypher/setup/:companyId" element={<Suspense fallback={<PageFallback />}><CypherSetupPage /></Suspense>} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  </React.StrictMode>,
);
